import React, { createContext, useContext } from "react";

import {
  TaskDetailsV2,
  TaskDetailsV3,
} from "@fartherfinance/frontend/api/Tasks/Types";
import { TaskId } from "@fartherfinance/frontend/api/Types";

import { NavigateTaskDetailsState, useTasksNavigation } from "./hooks";

interface TaskNavigationContextType {
  navigateToTask: (taskId: TaskId, state?: NavigateTaskDetailsState) => void;
  navigateToSubtaskCreation: (
    taskDetails: TaskDetailsV2 | TaskDetailsV3
  ) => void;
  navigateToTaskCreation: () => void;
  // Link-compatible API - returns just the pathname
  getTaskPathname: (taskId: TaskId) => string;
}

const TaskNavigationContext = createContext<
  TaskNavigationContextType | undefined
>(undefined);

interface TaskNavigationProviderProps {
  children: React.ReactNode;
  navigateToTask?: (taskId: TaskId, state?: NavigateTaskDetailsState) => void;
  navigateToSubtaskCreation?: (
    taskDetails: TaskDetailsV2 | TaskDetailsV3
  ) => void;
  navigateToTaskCreation?: () => void;
  getTaskPathname?: (taskId: TaskId) => string;
}

export const TaskNavigationProvider: React.FC<TaskNavigationProviderProps> = ({
  children,
  navigateToTask,
  navigateToSubtaskCreation,
  navigateToTaskCreation,
  getTaskPathname,
}) => {
  const defaultNavigation = useTasksNavigation();

  // Default implementation for getTaskPathname
  const defaultGetTaskPathname = (taskId: TaskId): string =>
    `/Advisor/Tasks/${taskId}`;

  const value: TaskNavigationContextType = {
    navigateToTask:
      navigateToTask || defaultNavigation.handleNavigateToTaskDetails,
    navigateToSubtaskCreation:
      navigateToSubtaskCreation ||
      defaultNavigation.handleNavigateToSubtaskCreation,
    navigateToTaskCreation:
      navigateToTaskCreation || defaultNavigation.handleNavigateToTaskCreation,
    getTaskPathname: getTaskPathname || defaultGetTaskPathname,
  };

  return (
    <TaskNavigationContext.Provider value={value}>
      {children}
    </TaskNavigationContext.Provider>
  );
};

// Export for potential future use, currently used internally
// eslint-disable-next-line import/no-unused-modules
export const useTaskNavigation = (): TaskNavigationContextType | null => {
  const context = useContext(TaskNavigationContext);
  return context || null;
};

/**
 * Backward-compatible hook that uses TaskNavigationContext if available,
 * otherwise falls back to the original useTasksNavigation behavior.
 * This ensures existing components continue to work without changes.
 */
export const useTaskNavigationCompat = () => {
  const contextNavigation = useTaskNavigation();
  const defaultNavigation = useTasksNavigation();

  if (contextNavigation) {
    return {
      handleNavigateToTaskDetails: contextNavigation.navigateToTask,
      handleNavigateToSubtaskCreation:
        contextNavigation.navigateToSubtaskCreation,
      handleNavigateToTaskCreation: contextNavigation.navigateToTaskCreation,
      handleNavigateToTasksList: defaultNavigation.handleNavigateToTasksList, // Always use default for tasks list
      getTaskPathname: contextNavigation.getTaskPathname,
    };
  }

  return {
    ...defaultNavigation,
    getTaskPathname: (taskId: TaskId): string => `/Advisor/Tasks/${taskId}`,
  };
};
